/**
 * `/dashboard` — the "My work" landing after authentication (routing-and-state.md §1;
 * UI/UX programme Flow 9). One request (`GET /dashboard`, membership-scoped server-side)
 * feeds the presentational `DashboardView`. Roles without `cases.list` (firm_admin) get
 * the administration landing instead — the endpoint would 403 them, so it is never
 * called. This screen is also where every permission guard bounces to, so a bounced user
 * lands on something useful, not a stub.
 */
import { Link } from '@tanstack/react-router';
import { Building2, Users } from '../components/ui/icons.ts';
import type { JSX, ReactNode } from 'react';

import { DashboardView } from '../components/dashboard/dashboard-view.tsx';
import { Card, CardBody, PageHeader } from '../components/ui/index.ts';
import { useDashboard } from '../hooks/use-dashboard.ts';
import { Can, usePermission } from '../lib/permissions/index.tsx';
import { getRequestId } from '../lib/request-id.ts';
import { useAuth } from '../store/auth-store.ts';

/** "Good morning, Ada" — time of day from the local clock, first name from the principal. */
export function greetingFor(fullName: string | undefined, hour = new Date().getHours()): string {
  const part = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const first = fullName?.trim().split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}

function Greeting(): ReactNode {
  const user = useAuth((s) => s.user);
  return greetingFor(user?.full_name);
}

function AdminLanding(): JSX.Element {
  return (
    <div className="argus-page">
      <PageHeader title="Dashboard" description={<Greeting />} />
      <div className="argus-form-grid">
        <Can permission="firm.read">
          <Card clickable>
            <CardBody className="stack-sm">
              <Building2 size={20} className="text-muted" aria-hidden="true" />
              <h2 className="argus-card__title">Firm settings & branding</h2>
              <p className="text-muted">Colours, logo and the details clients see.</p>
              <Link to="/settings/firm">Firm settings & branding →</Link>
            </CardBody>
          </Card>
        </Can>
        <Can permission="users.read">
          <Card clickable>
            <CardBody className="stack-sm">
              <Users size={20} className="text-muted" aria-hidden="true" />
              <h2 className="argus-card__title">Team</h2>
              <p className="text-muted">Members, roles and authenticator resets.</p>
              <Link to="/settings/team">Manage the team →</Link>
            </CardBody>
          </Card>
        </Can>
      </div>
    </div>
  );
}

export function DashboardScreen(): JSX.Element {
  const canListCases = usePermission('cases.list');
  const canCreateCase = usePermission('cases.create');
  const canReadFirm = usePermission('firm.read');
  const canReadUsers = usePermission('users.read');
  const query = useDashboard(canListCases);

  if (!canListCases) return <AdminLanding />;

  return (
    <DashboardView
      data={query.data}
      error={query.isError}
      errorRequestId={getRequestId(query.error)}
      onRetry={() => void query.refetch()}
      retrying={query.isFetching}
      canCreateCase={canCreateCase}
      showFirmSettings={canReadFirm}
      showTeam={canReadUsers}
      greeting={<Greeting />}
    />
  );
}
